import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, Users, HeartHandshake, MapPin, Sun, Stethoscope,
  Sparkles, Home as HomeIcon, Check, Quote,
} from "lucide-react";
import api from "@/lib/api";
import FaqSection from "@/components/FaqSection";
import SarapeDivider from "@/components/SarapeDivider";
import Seo from "@/components/Seo";
import RelatedGuides from "@/components/RelatedGuides";

const GROUP_ICONS = [Users, HeartHandshake, HomeIcon];

export default function MeridaPage() {
  const [p, setP] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/content/merida-profile").then((res) => setP(res.data));
  }, []);

  if (!p) {
    return <div className="mx-auto max-w-7xl px-6 py-32 text-charcoal-500">Loading…</div>;
  }

  return (
    <div data-testid="page-merida">
      <Seo
        title={p.hero_title}
        description="Mérida, Yucatán: safe, warm, welcoming — and Hola Movers' home base. Neighborhoods, costs, healthcare, and community for US residents moving to Mexico."
        image={p.hero_image}
        path="/merida"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.hero_image} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-sand-100 via-sand-100/85 to-sand-100/30" />
        </div>
        <div className="absolute inset-0 pattern-talavera opacity-[0.05]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">
              Our home base
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              {p.hero_title}
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">{p.hero_subtitle}</p>
          </div>
        </div>
      </section>
      <SarapeDivider />

      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24 space-y-20 lg:space-y-28">
        {/* Intro */}
        {p.intro && (
          <p className="max-w-3xl text-xl leading-relaxed text-charcoal-700 font-serif font-light">
            {p.intro}
          </p>
        )}

        {/* Quick facts */}
        {(p.stats || []).length > 0 && (
          <section data-testid="merida-stats">
            <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">Quick facts</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {p.stats.map((s, i) => (
                <div key={i} className="rounded-2xl border border-sand-300 bg-white p-5 shadow-[0_4px_20px_rgba(42,38,36,0.04)]">
                  <p className="text-xs uppercase tracking-wider text-clay-700 font-medium">{s.label}</p>
                  <p className="mt-2 text-lg text-charcoal-800 leading-snug">{s.value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Why Mérida */}
        {(p.why || []).length > 0 && (
          <section data-testid="merida-why">
            <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">Why many people choose Mérida</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {p.why.map((w, i) => (
                <div key={i} className="rounded-3xl bg-sage-light p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-terracotta">
                    <Shield size={20} strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-5 text-lg font-medium text-charcoal-800">{w.title}</h3>
                  <p className="mt-2 text-charcoal-600 leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Welcome / inclusivity */}
        {p.welcome && (
          <section data-testid="merida-welcome" className="rounded-[2rem] bg-charcoal-900 text-sand-100 overflow-hidden relative">
            <div className="absolute inset-0 pattern-talavera opacity-[0.06]" aria-hidden="true" />
            <div className="relative p-10 lg:p-16">
              <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/90 px-4 py-1.5 text-sm font-medium text-white">
                <Sparkles size={15} strokeWidth={1.5} /> Everyone's welcome
              </span>
              <h2 className="mt-6 text-3xl sm:text-4xl font-light tracking-tight max-w-2xl">{p.welcome.title}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-sand-100/80">{p.welcome.intro}</p>
              <div className="mt-10 grid md:grid-cols-3 gap-6">
                {(p.welcome.groups || []).map((g, i) => {
                  const Icon = GROUP_ICONS[i % GROUP_ICONS.length];
                  return (
                    <div key={i} className="rounded-2xl bg-white/[0.06] border border-white/10 p-7 backdrop-blur-sm">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-clay-500/30 text-clay-200">
                        <Icon size={20} strokeWidth={1.5} />
                      </span>
                      <h3 className="mt-5 text-lg font-medium text-white">{g.title}</h3>
                      <p className="mt-2 leading-relaxed text-sand-100/75">{g.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Expat community */}
        {p.expat && (
          <section data-testid="merida-expat" className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta-light text-terracotta">
                <Users size={22} strokeWidth={1.5} />
              </span>
              <h2 className="mt-6 text-2xl sm:text-3xl font-normal text-charcoal-800">{p.expat.title}</h2>
              <p className="mt-4 text-lg text-charcoal-600 leading-relaxed">{p.expat.text}</p>
            </div>
            <ul className="space-y-3">
              {(p.expat.ways || []).map((w, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl border border-sand-300 bg-white p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage-700">
                    <Check size={14} strokeWidth={2} />
                  </span>
                  <span className="text-charcoal-700 leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Testimonials */}
        {(p.testimonials || []).length > 0 && (
          <section data-testid="merida-testimonials">
            <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-3">{p.testimonials_title || "In their own words"}</h2>
            {p.testimonials_intro && (
              <p className="text-charcoal-600 mb-8 max-w-2xl">{p.testimonials_intro}</p>
            )}
            <div className="grid md:grid-cols-3 gap-6">
              {p.testimonials.map((t, i) => (
                <figure key={i} className="rounded-3xl border border-sand-300 bg-white p-8 shadow-[0_4px_20px_rgba(42,38,36,0.04)] flex flex-col">
                  <Quote size={26} strokeWidth={1.5} className="text-clay-500" />
                  <blockquote className="mt-4 flex-1 text-charcoal-700 leading-relaxed font-serif font-light text-lg">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-5 border-t border-sand-200 pt-4">
                    <p className="font-medium text-charcoal-800">{t.name}</p>
                    {t.detail && <p className="text-sm text-charcoal-500">{t.detail}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Neighborhoods */}
        {(p.neighborhoods || []).length > 0 && (          <section data-testid="merida-neighborhoods">
            <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-3">Neighborhood mini-guide</h2>
            <p className="text-charcoal-600 mb-8 max-w-2xl">A quick feel for some of Mérida's best-loved areas — so you can picture where you might land.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {p.neighborhoods.map((n, i) => (
                <div key={i} className="rounded-3xl border border-sand-300 bg-white p-7 shadow-[0_4px_20px_rgba(42,38,36,0.04)]">
                  <div className="flex items-center gap-2 text-terracotta">
                    <MapPin size={17} strokeWidth={1.5} />
                    <span className="text-xs uppercase tracking-wider font-medium">{n.character}</span>
                  </div>
                  <h3 className="mt-3 text-xl font-medium text-charcoal-800">{n.name}</h3>
                  <p className="mt-2 text-charcoal-600 leading-relaxed">{n.blurb}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Cost snapshot */}
        {(p.costs || []).length > 0 && (
          <section data-testid="merida-costs">
            <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-3">Cost snapshot</h2>
            <p className="text-charcoal-600 mb-8 max-w-2xl">Real, ballpark ranges to set expectations. Want a personalized estimate? Try the <Link to="/cost-calculator" className="text-terracotta underline underline-offset-2 font-medium">cost calculator</Link>.</p>
            <div className="rounded-3xl border border-sand-300 bg-white overflow-hidden divide-y divide-sand-200">
              {p.costs.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-4 px-6 py-4">
                  <span className="text-charcoal-700">{c.label}</span>
                  <span className="text-charcoal-900 font-medium whitespace-nowrap">{c.range}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Climate / Healthcare / Community */}
        <section className="grid md:grid-cols-3 gap-6" data-testid="merida-living">
          {[
            { icon: Sun, title: "Climate", text: p.climate },
            { icon: Stethoscope, title: "Healthcare", text: p.healthcare },
            { icon: Users, title: "Community", text: p.community },
          ].filter((b) => b.text).map((b, i) => (
            <div key={i} className="rounded-3xl bg-sand-50 border border-sand-200 p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-clay-700">
                <b.icon size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-5 text-lg font-medium text-charcoal-800">{b.title}</h3>
              <p className="mt-2 text-charcoal-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </section>

        {/* Compare note */}
        {p.compare_note && (
          <section className="max-w-3xl">
            <div className="rounded-2xl border-l-4 border-terracotta bg-terracotta-light/40 px-6 py-5">
              <h3 className="font-medium text-charcoal-800 mb-1">Considering another location?</h3>
              <p className="text-charcoal-600 leading-relaxed">{p.compare_note}</p>
            </div>
          </section>
        )}
      </div>

      <FaqSection page="merida" />

      <RelatedGuides slug="merida" />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-8">
        <div className="rounded-3xl bg-sage-light p-10 lg:p-16 text-center">
          <h2 className="text-3xl font-normal text-charcoal-800">Want to talk through your move to Mérida?</h2>
          <p className="mt-3 text-charcoal-600 max-w-xl mx-auto">
            Book a 45-minute intro call, or send us a message — whatever feels right for you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" data-testid="merida-book-cta" className="rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              Book an intro call
            </Link>
            <Link to="/contact" data-testid="merida-contact-cta" className="rounded-full border border-charcoal-300 px-8 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
              Send a message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
